import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  HiArrowLeft,
  HiDocumentDuplicate,
  HiInformationCircle,
} from "react-icons/hi2";
import { EditorState, convertFromRaw } from "draft-js";
import ViewEditor from "../../components/common/ViewEditor";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import { routes } from "../../helper/routes";
import {
  ExamGradingProvider,
  useExamGradingContext,
} from "../../context/Exam/ExamGradingContext";
import Spinner2 from "../../components/Spinner2";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import "./AssessShared.css";
import "./ExamSection.css";
import "./ResultSheet.css";
import StudentPaperHeader from "./StudentPaperHeader";
import { getSubmissionAttachments } from "./helpers/submissionAnswers";

function safeParseDraftList(content) {
  try {
    const parsed = JSON.parse(content || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function renderModelAnswer(row, onFileDownload) {
  if (row.questionType === "Submission") {
    const attachmentNames =
      row.modelAnswerFiles?.length > 0
        ? row.modelAnswerFiles
        : getSubmissionAttachments(row.modelAnswerRaw);
    return (
      <div className="result-sheet-answer-panel__copy">
        {attachmentNames.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {attachmentNames.map((fileName) => (
              <button
                type="button"
                key={fileName}
                className="exam-paper-chip"
                onClick={() =>
                  onFileDownload(row.question_id, fileName, true)
                }
              >
                {fileName}
              </button>
            ))}
          </div>
        ) : null}
        {row.modelAnswerText ? <p>{row.modelAnswerText}</p> : null}
        {!row.modelAnswerText && attachmentNames.length === 0 ? "No model answer available." : null}
      </div>
    );
  }
  return renderDraftContent(row.modelAnswerRaw) || "No model answer available.";
}

function toEditorState(raw) {
  try {
    if (!raw) return EditorState.createEmpty();
    return EditorState.createWithContent(convertFromRaw(raw));
  } catch {
    return EditorState.createEmpty();
  }
}

function renderDraftContent(content, className = "") {
  const parts = safeParseDraftList(content);
  if (!parts.length) return null;

  return (
    <div className={className}>
      {parts.map((raw, index) => (
        <ViewEditor key={index} editorState={toEditorState(raw)} />
      ))}
    </div>
  );
}

function groupByPassage(questionGroups = []) {
  const groups = [];

  questionGroups.forEach((questionGroup) => {
    if (!questionGroup.passageId) {
      groups.push({
        passageId: null,
        passageRaw: "",
        questions: [questionGroup],
      });

      return;
    }

    const existingGroup = groups.find(
      (group) =>
        String(group.passageId) ===
        String(questionGroup.passageId)
    );

    if (existingGroup) {
      existingGroup.questions.push(questionGroup);
      return;
    }

    groups.push({
      passageId: questionGroup.passageId,
      passageRaw: questionGroup.passageRaw,
      questions: [questionGroup],
    });
  });

  return groups;
}

function renderSingleDraftRaw(raw, className = "") {
  if (!raw) return null;

  try {
    return (
      <div className={className}>
        <ViewEditor editorState={toEditorState(raw)} />
      </div>
    );
  } catch {
    return null;
  }
}

function ResultSheetContent() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [paperHeaderAssessment, setPaperHeaderAssessment] = useState(null);

  const exam = location.state?.exam || {
    candidate_id: Number(id),
    attempt_no: Number(new URLSearchParams(location.search).get("attempt_no") || 1),
    assessment_id: Number(new URLSearchParams(location.search).get("assessment_id")),
  };

  const {
    report,
    loadReport,
    loadGradingPaper,
    buildSelfGradingDataset,
    selfGradingQuestions,
    selfGradingSummary,
    reportLoading,
  } = useExamGradingContext();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!exam?.candidate_id || !exam?.assessment_id) return;

      const reportData = await loadReport(exam);
      const gradingData = await loadGradingPaper(exam);

      const assessmentDetails = gradingData.assessment_details;

      const startTime = reportData.attempt_start_time;

      if (!cancelled) {
        setPaperHeaderAssessment({
          ...assessmentDetails,
          assessment_details: assessmentDetails,
          start_time: startTime,
          is_set_time_limit: exam?.is_set_time_limit,
          exam_time_interval: exam?.exam_time_interval,
          exam_time_interval_unit_id: exam?.exam_time_interval_unit_id,
        });

        buildSelfGradingDataset(gradingData);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam?.candidate_id, exam?.assessment_id, exam?.attempt_no]);

  // const summary = selfGradingSummary || {
  //   total: 0,
  //   currentMarks: 0,
  //   totalMarks: 0,
  //   percentage: "-",
  // };

  const reportMarks =
    report?.total_mark_obtained ??
    report?.final_grade ??
    0;

  const reportTotalMarks =
    report?.total_mark_allocation ??
    0;

  const summary = {
    total: selfGradingSummary?.total || 0,
    currentMarks:
      selfGradingSummary?.currentMarks ||
      Number(reportMarks || 0),
    totalMarks:
      selfGradingSummary?.totalMarks ||
      Number(reportTotalMarks || 0),
    percentage: selfGradingSummary?.percentage || "-",
  };

  const handleFileDownload = async (
    questionId,
    fileName,
    isModelAnswer = false
  ) => {
    try {
      const containerName = config.storageBlobConfigs.containerName;
      const cleanFileName = String(fileName)
        .replace(/['"]+/g, "")
        .trim();
      // OLD WORKING WAY for model answers
      const blobName = isModelAnswer
        ? cleanFileName
        : `${exam.candidate_id}-${questionId}-${cleanFileName}`;

      await AzureStorage.downloadBlob(containerName, blobName);
    } catch (error) {
      console.error("File download failed", error);
      window.Swal.fire(
        "Failed!",
        "File download failed. Please try again.",
        "error"
      );
    }
  };

  return (
    <ContentWrapper>
      <section className="result-sheet-page">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-11 col-xxl-10">
            <div className="exam-paper-page__main result-sheet-shell">
              <div className="exam-paper-page__content">
                <div className="exam-paper-page__title-row">
                  <button
                    type="button"
                    className="exam-paper-page__back-btn"
                    onClick={() =>
                      history.push({
                        pathname: routes.exm_grade_report.replace(":id", exam.candidate_id),
                        search: `?assessment_id=${exam.assessment_id}&attempt_no=${exam.attempt_no || 1}`,
                        state: { exam },
                      })
                    }
                    aria-label="Back to grade report"
                    title="Back to grade report"
                  >
                    <HiArrowLeft aria-hidden="true" />
                  </button>

                  {/* <div className="exam-paper-page__title">
                    <h1>Result Sheet</h1>
                    <p>Review the full paper, submitted answers, model answers, guidance, and marks.</p>
                  </div> */}

                  <div className="exam-paper-page__title">
                    {paperHeaderAssessment && (
                      <StudentPaperHeader
                        assessment={paperHeaderAssessment}
                        subtitle="Review candidate answers, model answers, guidelines, and enter marks for manual questions."
                        showStartTime={true}
                      />
                    )}
                  </div>
                </div>

                <div className="row g-3 result-sheet-summary">
                  <div className="col-12 col-md-4">
                    <div className="result-sheet-summary-card">
                      <span>Total Questions</span>
                      <strong>{summary.total}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="result-sheet-summary-card">
                      <span>Total Marks</span>
                      <strong>{summary.totalMarks}</strong>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="result-sheet-summary-card">
                      <span>Score</span>
                      <strong>
                        {summary.currentMarks}/{summary.totalMarks}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="exam-paper-page__questions">
                  {reportLoading ? (
                    <Spinner2 text="Loading" />
                  ) : selfGradingQuestions.length === 0 ? (
                    <p></p>
                  ) : (
                    selfGradingQuestions.map((partGroup) => {
                      const sections = Array.isArray(partGroup.sections) ? partGroup.sections : [];

                      return (
                        <div className="exam-part-block" key={partGroup.partKey}>
                          {partGroup.partName && (
                            <div className="exam-part-header">
                              <h2>{partGroup.partName}</h2>
                              {partGroup.partInstruction && <p>{partGroup.partInstruction}</p>}
                            </div>
                          )}

                          {sections.map((sectionGroup) => {
                            const questionGroups = Array.isArray(sectionGroup.questionGroups)
                              ? sectionGroup.questionGroups
                              : [];

                            return (
                              <div className="exam-section-block" key={sectionGroup.sectionKey}>
                                {sectionGroup.sectionName && (
                                  <div className="exam-section-header">
                                    <h3>{sectionGroup.sectionName}</h3>
                                    {sectionGroup.sectionInstruction && <p>{sectionGroup.sectionInstruction}</p>}
                                  </div>
                                )}

                                {groupByPassage(questionGroups).map(
                                  (passageGroup, passageIndex) => (
                                    <React.Fragment
                                      key={
                                        passageGroup.passageId
                                          ? `passage-${passageGroup.passageId}`
                                          : `question-${passageIndex}`
                                      }
                                    >
                                      {passageGroup.passageId && passageGroup.passageRaw && (
                                        <div className="exam-question-card result-sheet-card">
                                          <div className="self-grading-answer-block self-grading-answer-block--reference">
                                            <div className="self-grading-reference-copy">
                                              {renderDraftContent(passageGroup.passageRaw)}
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                      {passageGroup.questions.map((group) => (
                                        <article
                                          className="exam-question-card result-sheet-card"
                                          key={`${partGroup.partKey}-${sectionGroup.sectionKey}-${group.parentQuestionId}`}
                                        >
                                          {(group.children || []).length > 1 && (
                                            <div className="exam-question-card__header">
                                              <div>
                                                <strong>Question {group.parentIndex + 1}</strong>
                                                {renderDraftContent(group.parentRaw, "self-grading-question-content")}
                                              </div>
                                            </div>
                                          )}
                                          {/* {group.passageRaw && (
                                            <div className="self-grading-answer-block self-grading-answer-block--reference">
                                              <span className="self-grading-answer-label">Passage</span>
                                              <div className="self-grading-reference-copy">
                                                {renderDraftContent(group.passageRaw)}
                                              </div>
                                            </div>
                                    )} */}

                                    {(group.children || []).map((row, childIndex) => (
                                      <div className="self-grading-subquestion-card" key={row.question_id}>
                                        <div className="exam-question-card__header">
                                          <div>
                                            <strong>
                                              {(group.children || []).length > 1
                                                ? `Subquestion ${childIndex + 1}`
                                                : `Question ${group.parentIndex + 1}`}
                                            </strong>
                                            {renderDraftContent(row.questionRaw, "self-grading-question-content")}
                                          </div>

                                          <span className="result-sheet-status-badge result-sheet-status-badge--correct">
                                            <HiDocumentDuplicate aria-hidden="true" />
                                            {Number(row.markObtained || 0)}/{row.markAllocation}
                                          </span>
                                        </div>
                                        {row.isMcq ? (
                                          <div>
                                            {(() => {
                                              const options = row.mcqOptions || [];
                                              const selectedAnswers = row.mcqSelectedIndexes || [];
                                              const correctIndexes = row.mcqCorrectIndexes || [];

                                              const sortedSelectedAnswers = [...selectedAnswers].sort((a, b) => a - b);
                                              const sortedCorrectIndexes = [...correctIndexes].sort((a, b) => a - b);

                                              const isCandidateCorrect =
                                                sortedSelectedAnswers.length === sortedCorrectIndexes.length &&
                                                sortedSelectedAnswers.every(
                                                  (answerIndex, index) => answerIndex === sortedCorrectIndexes[index]
                                                );

                                              return (
                                                <>
                                                  <div className="row g-3">
                                                    {options.map((option) => {
                                                      const optionIndex = option.index;
                                                      const isSelected = selectedAnswers.includes(optionIndex);
                                                      const isCorrectAnswer = correctIndexes.includes(optionIndex);
                                                      const isWrongSelected = isSelected && !isCorrectAnswer;

                                                      const optionClassName = [
                                                        "exam-option",
                                                        "result-sheet-option",
                                                        isCorrectAnswer ? "result-sheet-option--correct" : "",
                                                        isWrongSelected ? "result-sheet-option--wrong" : "",
                                                      ]
                                                        .filter(Boolean)
                                                        .join(" ");

                                                      return (
                                                        <div
                                                          className="col-12 col-md-6"
                                                          key={`${row.question_id}-${optionIndex}`}
                                                        >
                                                          <div className={optionClassName}>
                                                            <span className="exam-option__indicator" />

                                                            <div className="result-sheet-option__copy">
                                                              <span className="exam-option__text">
                                                                {renderSingleDraftRaw(option.raw)}
                                                              </span>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>

                                                  <div
                                                    className={`result-sheet-mcq-result ${isCandidateCorrect
                                                        ? "result-sheet-mcq-result--correct"
                                                        : "result-sheet-mcq-result--wrong"
                                                      }`}
                                                  >
                                                    <strong>
                                                      {isCandidateCorrect ? "Correct answer" : "Wrong answer"}
                                                    </strong>

                                                    <div className="result-sheet-mcq-result__answers">
                                                      <div>
                                                        <span>Candidate Answer</span>
                                                        <p>{row.candidateAnswerText || "No answer selected."}</p>
                                                      </div>

                                                      <div>
                                                        <span>Model Answer</span>
                                                        <p>{row.modelAnswerText || "No model answer available."}</p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </>
                                              );
                                            })()}
                                          </div>
                                        ) : (


                                          <>
                                            <div className="result-sheet-answer-panel">
                                              <span className="result-sheet-answer-panel__label">Candidate Answer</span>

                                              <div className="result-sheet-answer-panel__copy">
                                                {row.candidateAnswerText ? <p>{row.candidateAnswerText}</p> : null}

                                                {row.candidateAnswerFiles?.length ? (
                                                  <div>
                                                    {row.candidateAnswerFiles.map((fileName) => (
                                                      <button
                                                        type="button"
                                                        key={fileName}
                                                        className="exam-paper-chip"
                                                        onClick={() => handleFileDownload(row.question_id, fileName)}
                                                      >
                                                        {fileName}
                                                      </button>
                                                    ))}
                                                  </div>
                                                ) : null}

                                                {!row.candidateAnswerText && !row.candidateAnswerFiles?.length
                                                  ? "No answer submitted."
                                                  : null}
                                              </div>
                                            </div>

                                            <div className="result-sheet-answer-panel result-sheet-answer-panel--reference">
                                              <span className="result-sheet-answer-panel__label">Model Answer</span>
                                              {renderModelAnswer(row, handleFileDownload)}
                                            </div>
                                          </>
                                        )}

                                        <div className="self-grading-feedback">
                                          <HiInformationCircle aria-hidden="true" />
                                          <span>
                                            {renderDraftContent(row.guidelineRaw) || "No guidelines available."}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                        </article>
                                      ))}
                                    </React.Fragment>
                                  )
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ContentWrapper>
  );
}

export default function ResultSheet() {
  return (
    <ExamGradingProvider>
      <ResultSheetContent />
    </ExamGradingProvider>
  );
}